import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useDebouncedValue from '../useDebouncedValue';
import useGetFlashbook from './useGetFlashbook';
import { getTags, getBusinessTags } from '../../services/flashbooks';
import useUser from '../global/useUser';
import useBusiness from '../businesses/useBusiness';
import { isUserAdminOrManager } from '../../utils/global/helper';
import { LoadedUser } from '../../interfaces';

function useGetTags({
  query,
  enabled = true,
  excludeTags,
}: {
  query?: string;
  enabled?: boolean;
  excludeTags?: string[];
}) {
  const { user } = useUser<LoadedUser>();
  const { isStudio, business } = useBusiness();

  const isStudioShopManager = isUserAdminOrManager(user?.role) && isStudio;

  const businessId = business?.id;

  const { data: flashbook } = useGetFlashbook({
    enabled: !isStudioShopManager,
  });

  const flashbookId = isStudioShopManager ? undefined : flashbook?.id;
  const lowercasedQuery = useMemo(() => query?.trim().toLowerCase(), [query]);
  const lowercasedExcludeTags = useMemo(
    () => [...(excludeTags ?? [])].map(tag => tag.trim().toLowerCase()).sort(),
    [excludeTags],
  );

  const debouncedQuery = useDebouncedValue(lowercasedQuery, 500);

  const isEnabled =
    !!enabled && (isStudioShopManager ? !!businessId : !!flashbook?.id);

  return useQuery({
    queryKey: [
      'flashbook-tags',
      {
        businessId,
        flashbookId,
        query: debouncedQuery,
        excludeTags: lowercasedExcludeTags,
        isStudioShopManager,
      },
    ],
    enabled: isEnabled,
    queryFn: () =>
      isStudioShopManager
        ? getBusinessTags({
            businessId: businessId!,
            query: debouncedQuery,
            excludeTags: lowercasedExcludeTags,
          })
        : flashbookId
        ? getTags({
            flashbookId,
            query: debouncedQuery,
            excludeTags: lowercasedExcludeTags,
          })
        : [],
  });
}

export default useGetTags;
